import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TrainingPlayer from "./TrainingPlayer";
import { fetchForPage, fetchPageLinks, fetchVideo, fmtTime, routeMatches } from "../../services/TrainingApi";
import "./Training.css";

/*
  تحديث 101 — زر «شاهد الشرح» (الصورة المعتمدة C): يظهر بجانب عنوان الصفحة فقط إن كان لها مقطع شرح مرتبط ومسموح للمستخدم.
  يفتح درجاً جانبياً فوق الصفحة نفسها (لا تنقّل ولا إعادة تحميل ولا تعتيم — يمكن متابعة العمل في الصفحة أثناء المشاهدة)
  فتبقى البيانات المدخلة كما هي، ويبدأ الفيديو من الموضوع المرتبط بالصفحة. Esc أو × للإغلاق.
  «فتح في مركز الشروحات» يفتح تبويباً جديداً لنفس السبب.
*/
let linksCache = null;
let linksAt = 0;
const listeners = new Set();
function loadLinks(force = false) {
  if (!force && linksCache && Date.now() - linksAt < 10 * 60 * 1000) return Promise.resolve(linksCache);
  return fetchPageLinks().then((l) => { linksCache = l; linksAt = Date.now(); listeners.forEach((f) => f(l)); return l; }).catch(() => linksCache || []);
}
/** بعد تعديل الشروحات من الإدارة: تحديث روابط الصفحات فوراً. */
export const refreshTrainingLinks = () => loadLinks(true);

export function WatchHelpButton({ portal = false, className = "" }) {
  const location = useLocation();
  const [links, setLinks] = useState(linksCache || []);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const f = (l) => setLinks(l);
    listeners.add(f);
    loadLinks().then(setLinks);
    return () => listeners.delete(f);
  }, []);
  useEffect(() => setOpen(false), [location.pathname]);
  const path = location.pathname;
  const has = useMemo(() => links.some((p) => routeMatches(p, path)), [links, path]);
  if (!has || /^\/(portal\/)?training/.test(path)) return null;
  return (
    <>
      <button type="button" className={`tr-help ${className}`} onClick={() => setOpen(true)} data-testid="watch-help" title="شاهد الشرح">
        <i className="fa-solid fa-play" aria-hidden="true" /> <span>شاهد الشرح</span>
      </button>
      {open && createPortal(<WatchHelpDrawer route={path} portal={portal} onClose={() => setOpen(false)} />, document.body)}
    </>
  );
}

function WatchHelpDrawer({ route, portal, onClose }) {
  const { i18n } = useTranslation();
  const en = (i18n.language || "ar").startsWith("en");
  const [items, setItems] = useState(null);
  const [videoId, setVideoId] = useState(null);
  const [video, setVideo] = useState(null);
  const [now, setNow] = useState(0);
  const player = useRef(null);
  const T = (x, f = "title") => (en ? x?.[`${f}En`] || x?.[`${f}Ar`] : x?.[`${f}Ar`]) || "";

  useEffect(() => {
    fetchForPage(route).then((l) => { setItems(l); if (l.length) setVideoId(l[0].videoId); }).catch(() => setItems([]));
  }, [route]);
  useEffect(() => {
    if (!videoId) return;
    setVideo(null);
    fetchVideo(videoId).then(setVideo).catch(() => setVideo(null));
  }, [videoId]);
  useEffect(() => {
    const k = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);

  const videos = useMemo(() => {
    const m = new Map();
    (items || []).forEach((i) => { if (!m.has(i.videoId)) m.set(i.videoId, i); });
    return [...m.values()];
  }, [items]);
  const linked = new Set((items || []).filter((i) => i.videoId === videoId).map((i) => i.chapterId));
  const first = (items || []).find((i) => i.videoId === videoId);
  const chapters = video?.chapters || [];
  const currentIdx = chapters.reduce((acc, c, i) => (Number(c.start) <= now + 0.25 ? i : acc), -1);
  const centre = `${portal ? "/portal/training" : "/training"}?v=${videoId || ""}&t=${Math.floor(player.current?.currentTime?.() || first?.start || 0)}`;

  return (
    <>
      <aside className="tr-drawer" role="dialog" aria-modal="false" aria-label="شاهد الشرح" dir={en ? "ltr" : "rtl"} data-testid="watch-help-drawer">
        <div className="hd">
          <i className="fa-solid fa-play" aria-hidden="true" />
          <b data-no-translate>شرح: {video ? T(video) : "…"}</b>
          {video && <span className="tr-pill" dir="ltr">{fmtTime(video.durationSeconds)}</span>}
          <button type="button" className="x" onClick={onClose} aria-label="إغلاق" data-testid="watch-help-close">×</button>
        </div>
        {videos.length > 1 && (
          <div className="tr-drawer__tabs">
            {videos.map((v) => (
              <button key={v.videoId} type="button" className={`tr-chip${v.videoId === videoId ? " on" : ""}`} onClick={() => setVideoId(v.videoId)} data-no-translate>
                {en ? v.videoTitleEn || v.videoTitleAr : v.videoTitleAr}
              </button>
            ))}
          </div>
        )}
        {items && items.length === 0 && <div className="tr-res__none">لا يوجد شرح لهذه الصفحة.</div>}
        {video && (
          <TrainingPlayer key={video.id} ref={player} video={video} lang={en ? "en" : "ar"} compact
            startAt={Number(first?.start || 0) || 0.01} autoPlay onTime={setNow} />
        )}
        <div className="tr-drawer__idx">
          {chapters.map((c, i) => (
            <button key={c.id} type="button" className={`tr-ch${i === currentIdx ? " on" : ""}`} onClick={() => player.current?.seek(Number(c.start))} data-testid="watch-help-chapter">
              <span className="tm" dir="ltr">{fmtTime(c.start)}</span>
              <span className="tt" data-no-translate>{T(c)}</span>
              {linked.has(c.id) && <span className="tr-here">هذه الصفحة</span>}
            </button>
          ))}
        </div>
        <div className="ft">
          الدرج فوق الصفحة — بياناتك المدخلة لا تُفقد.{" "}
          <a href={centre} target="_blank" rel="noopener noreferrer" data-testid="watch-help-open-centre">فتح في مركز الشروحات ↗</a>
        </div>
      </aside>
    </>
  );
}
